import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Check, X as XIcon, Edit3, Save, Image as ImageIcon, Clock,
  ChevronDown, ChevronUp, Users, Camera, Smartphone, AtSign, MessageCircle,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/services/adminApi';
import LoadingSpinner from '@/components/common/LoadingSpinner';

interface ContentPost {
  postId: string;
  platform: string;
  postText: string;
  category: string;
  hookType: string | null;
  sortOrder: number;
  parentPostId: string | null;
  scheduledFor: string | null;
  imageTemplate: string | null;
  imageHeadline: string | null;
  imageBody: string | null;
  imageAccent: string | null;
  imagePath: string | null;
  imageRenderedAt: string | null;
  status: string;
  createdAt: string;
}

interface ContentBatch {
  batchId: string;
  weekStartDate: string;
  planSummary: string | null;
  status: string;
  totalPosts: number;
  imagesRendered: number;
  notificationSentAt: string | null;
  createdAt: string;
  finalizedAt: string | null;
  posts: ContentPost[];
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  pending_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  published: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const PLATFORM_NAMES: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  instagram_story: 'Instagram Story',
  twitter: 'X (Twitter)',
  twitter_thread: 'X Thread',
};

// lucide-react removed its brand icons (Facebook, Instagram) for trademark
// reasons, and this branch upgraded to 1.21.0. The platform LABELS below are
// unchanged -- only the glyphs are substituted, with the closest generic icons.
const PLATFORM_ICON_COMPONENTS: Record<string, typeof Users> = {
  facebook: Users,
  instagram: Camera,
  instagram_story: Smartphone,
  twitter: AtSign,
  twitter_thread: MessageCircle,
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || STATUS_COLORS.draft}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function PostCard({
  post,
  onUpdate,
}: {
  post: ContentPost;
  onUpdate: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.postText);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleSave = async () => {
    if (editText === post.postText) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateContentPost(post.postId, { postText: editText });
      setEditing(false);
      onUpdate();
    } catch {
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    try {
      await adminApi.updateContentPost(post.postId, { status });
      onUpdate();
    } catch {
      alert('Failed to update status');
    }
  };

  const isLong = post.postText.length > 200;
  const displayText = isLong && !expanded ? post.postText.slice(0, 200) + '...' : post.postText;

  return (
    <div className="border border-border rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {(() => { const Icon = PLATFORM_ICON_COMPONENTS[post.platform] || AtSign; return <Icon className="w-4 h-4 text-muted-foreground" />; })()}
          <span className="text-sm font-medium">{PLATFORM_NAMES[post.platform] || post.platform}</span>
          <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
            {post.category.replace(/_/g, ' ')}
          </span>
          {post.hookType && (
            <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
              {post.hookType}
            </span>
          )}
        </div>
        <StatusBadge status={post.status} />
      </div>

      {/* Post text */}
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[120px] resize-y"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving}>
              <Save className="w-3.5 h-3.5 mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditText(post.postText); }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{displayText}</p>
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-primary hover:underline mt-1 flex items-center gap-1"
            >
              {expanded ? <><ChevronUp className="w-3 h-3" /> Show less</> : <><ChevronDown className="w-3 h-3" /> Show more</>}
            </button>
          )}
        </div>
      )}

      {/* Image preview */}
      {post.imagePath && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
          <img
            src={post.imagePath}
            alt="Post image"
            className="w-20 h-20 rounded object-cover border border-border"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="text-xs text-muted-foreground">
            <div className="flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {post.imageTemplate}</div>
            {post.imageHeadline && <div className="mt-1 truncate max-w-xs">{post.imageHeadline}</div>}
          </div>
        </div>
      )}

      {/* Scheduled time */}
      {post.scheduledFor && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          Scheduled: {new Date(post.scheduledFor).toLocaleString()}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
        )}
        {post.status === 'pending_review' && (
          <>
            <Button size="sm" onClick={() => handleStatusChange('approved')}>
              <Check className="w-3.5 h-3.5 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleStatusChange('draft')}>
              <XIcon className="w-3.5 h-3.5 mr-1" /> Reject
            </Button>
          </>
        )}
        {post.status === 'approved' && (
          <Button size="sm" variant="outline" onClick={() => handleStatusChange('pending_review')}>
            Unapprove
          </Button>
        )}
      </div>
    </div>
  );
}

export function ContentBatchPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const [batch, setBatch] = useState<ContentBatch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (batchId) loadBatch();
  }, [batchId]);

  const loadBatch = async () => {
    if (!batchId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getContentBatch(batchId);
      setBatch(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load batch');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBatch = async () => {
    if (!batchId || !batch) return;
    if (!confirm(`Approve all ${batch.totalPosts} posts in this batch?`)) return;
    try {
      await adminApi.updateContentBatch(batchId, { status: 'approved' });
      loadBatch();
    } catch {
      alert('Failed to approve batch');
    }
  };

  const handleRejectBatch = async () => {
    if (!batchId) return;
    try {
      await adminApi.updateContentBatch(batchId, { status: 'draft' });
      loadBatch();
    } catch {
      alert('Failed to reject batch');
    }
  };

  if (loading) return <LoadingSpinner message="Loading batch..." />;

  if (error || !batch) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-4">{error || 'Batch not found'}</p>
        <Link to="/content" className="text-primary hover:underline">Back to Content</Link>
      </div>
    );
  }

  // Group posts by platform (derived from data, not hardcoded list)
  const postsByPlatform: Record<string, ContentPost[]> = {};
  for (const post of batch.posts) {
    if (!postsByPlatform[post.platform]) postsByPlatform[post.platform] = [];
    postsByPlatform[post.platform].push(post);
  }
  const platforms = Object.keys(postsByPlatform);

  const pendingCount = batch.posts.filter(p => p.status === 'pending_review').length;
  const approvedCount = batch.posts.filter(p => p.status === 'approved').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/content" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2">
            <ArrowLeft className="w-4 h-4" /> Back to Content
          </Link>
          <h1 className="text-3xl font-bold text-foreground">
            Week of {batch.weekStartDate}
          </h1>
          {batch.planSummary && (
            <p className="text-muted-foreground mt-1">{batch.planSummary}</p>
          )}
        </div>
        <StatusBadge status={batch.status} />
      </div>

      {/* Stats + Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex gap-6">
              <div>
                <span className="text-2xl font-bold">{batch.totalPosts}</span>
                <span className="text-sm text-muted-foreground ml-1">posts</span>
              </div>
              <div>
                <span className="text-2xl font-bold">{batch.imagesRendered}</span>
                <span className="text-sm text-muted-foreground ml-1">images</span>
              </div>
              <div>
                <span className="text-2xl font-bold text-green-600">{approvedCount}</span>
                <span className="text-sm text-muted-foreground ml-1">approved</span>
              </div>
              <div>
                <span className="text-2xl font-bold text-yellow-600">{pendingCount}</span>
                <span className="text-sm text-muted-foreground ml-1">pending</span>
              </div>
            </div>
            <div className="flex gap-2">
              {batch.status === 'pending_review' && (
                <>
                  <Button onClick={handleApproveBatch}>
                    <Check className="w-4 h-4 mr-1" /> Approve All
                  </Button>
                  <Button variant="outline" onClick={handleRejectBatch}>
                    <XIcon className="w-4 h-4 mr-1" /> Send Back
                  </Button>
                </>
              )}
              {batch.status === 'approved' && (
                <Button variant="outline" onClick={handleRejectBatch}>
                  Unapprove Batch
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Posts by platform */}
      {platforms.map(platform => (
        <Card key={platform}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {(() => { const Icon = PLATFORM_ICON_COMPONENTS[platform] || AtSign; return <Icon className="w-5 h-5" />; })()}
              {PLATFORM_NAMES[platform] || platform}
              <span className="text-sm font-normal text-muted-foreground">
                ({postsByPlatform[platform].length} post{postsByPlatform[platform].length !== 1 ? 's' : ''})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {postsByPlatform[platform].map(post => (
              <PostCard
                key={post.postId}
                post={post}
                onUpdate={loadBatch}
              />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
